/* eslint-disable @reatom/unit-naming-rule */

import { atom, withAssign } from '@reatom/framework'

import { AsyncStateN, Result } from '@blade/std-lib'
import { selectedNetworkAtom } from '@root/entities/network'
import { Network } from '@root/entities/network/model'
import { deviceWalletIdsAtom } from '@root/features/secure-storage/atoms'
import { reatomResource } from '@root/shared/lib'

import { Summary } from '../model'

import { fetchUserSummaryRequestAction } from './summary-requests'

import type { Atom } from '@reatom/framework'
import type { WalletId } from '@root/entities/wallet'

const reatomSummaryChartData = ({
  summariesAtom,
}: {
  summariesAtom: Atom<AsyncStateN.AsyncStateN<unknown, Array<Summary.Summary>>>
}) => {
  const chartDataAtom = atom((ctx) => {
    const state = ctx.spy(summariesAtom)

    if (!state.value) {
      return undefined
    }

    return Summary.createChartData(state.value, new Date())
  }, 'chartDataAtom')

  const priceChangeStateAtom = atom((ctx) => {
    const state = ctx.spy(summariesAtom)

    return state.pipe(
      AsyncStateN.map((it) => {
        return Summary.calculateSummaryPriceChange(it)
      }),
    )
  }, 'priceChangeStateAtom')

  return {
    chartDataAtom,
    priceChangeStateAtom,
  }
}

const reatomWalletsSummary = ({ network }: { network: Network.Network }) => {
  const stateAtom = reatomResource(
    async (ctx) => {
      const walletIds = ctx.spy(deviceWalletIdsAtom)

      if (walletIds.size === 0) {
        return Result.succeed([])
      }

      const result = await ctx.schedule(() =>
        fetchUserSummaryRequestAction(ctx, {
          network: network,
          walletIds: [...walletIds],
        }),
      )

      return result
    },
    {
      name: `reatomWalletsSummary-${network}`,
      reconnectStrategy: 'refresh',
    },
  )

  const { chartDataAtom, priceChangeStateAtom } = reatomSummaryChartData({
    summariesAtom: stateAtom,
  })

  return stateAtom.pipe(
    withAssign(() => ({
      chartData: chartDataAtom,
      priceChangeState: priceChangeStateAtom,
    })),
  )
}

export const reatomWalletSummary = (walletId: WalletId) => {
  const stateAtom = atom((ctx) => {
    const summaryState = ctx.spy(summaryStateAtom)
    return summaryState.pipe(
      AsyncStateN.map((it) => {
        return Summary.createWalletSummaries(walletId, it)
      }),
    )
  }, 'walletSummariesAtom')

  const { chartDataAtom, priceChangeStateAtom } = reatomSummaryChartData({
    summariesAtom: stateAtom,
  })

  return stateAtom.pipe(
    withAssign(() => ({
      chartData: chartDataAtom,
      priceChangeState: priceChangeStateAtom,
    })),
  )
}

export const summaryModelByNetworkAtom = Network.byNetworkRecord((network) =>
  reatomWalletsSummary({ network }),
)

export const summaryModelAtom = atom((ctx) => {
  const network = ctx.spy(selectedNetworkAtom)
  const state = summaryModelByNetworkAtom[network]
  return state
})

export const summaryStateAtom = atom((ctx) => {
  return ctx.spy(ctx.spy(summaryModelAtom))
})
